import React, { ReactNode, useEffect, useState } from 'react';
import ToastAlert, { InlineAlert } from '@saucelabs/ds-alert';
import { constants as sentryConstants, Sentry } from '@saucelabs/sl-util-sentry';
import { Link } from '@saucelabs/ds-link';

import { BuildStatus } from '../../graphql/__generated__/graphql';
import { VisualErrorCodes } from '../../global/constants';

const checkIsErrorCode = (code: string): code is VisualErrorCodes => Object
  .keys(VisualErrorCodes)
  .includes(code);

type Props = {
  status: BuildStatus,
  error?: Record<'code', VisualErrorCodes>
  className?: string;
}

const DiffHeaderStatus: React.FC<Props> = ({ status, error }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const isRunning = status === BuildStatus.Running;
  const isErrored = status === BuildStatus.Errored;
  const isSuccess = status === BuildStatus.Equal || status === BuildStatus.Unapproved;

  const errorCode = error && 'code' in error ? error.code : '';
  const isErrorCode = checkIsErrorCode(errorCode);

  let errorMsg = '';
  let errorDescription: ReactNode;

  // See graphql types for build 'Error'
  switch (errorCode) {
    case 'TIMEOUT':
      errorMsg = 'Visual build was not completed within the time limit';
      errorDescription = (
        <span>
          The build timed out because
          {' '}
          <code>finishBuild</code>
          {' '}
          was not called. This could be due to a failed or erroneous test execution. Check the
          output of your test runner for more details.
        </span>
      );
      break;
    case 'DIFF_TIMEOUT':
      errorMsg = 'Diffs failed to be computed';
      errorDescription = (
        <span>
          One or more diffs failed to be computed within the time limit. This could be due a very
          large screenshot, a corrupt screenshot file, an unsupported image format or an internal
          error.
          {' '}
          <Link
            isExternal
            size="large"
            target="_blank"
            to="https://support.saucelabs.com/hc/en-us"
          >
            Contact support
          </Link>
          {' '}
          if the issue persists.
        </span>
      );
      break;
    default: {
      errorMsg = 'Visual build encountered an unknown error during execution';
      let errorJson = 'Unknown error.';

      try {
        errorJson = JSON.stringify(error);
        // eslint-disable-next-line no-empty,id-length
      } catch (e) {}

      errorDescription = (
        <span>
          One or more errors occurred during visual build execution. See the error message below for
          more details:
          <br />
          <code>
            {errorJson}
          </code>
        </span>
      );
    }
      break;
  }

  useEffect(() => {
    if (isRunning) {
      setShowSuccess(true);
    } else if (!isSuccess) {
      setShowSuccess(false);
    }
  }, [isRunning, isSuccess, status]);

  useEffect(() => {
    if (!isErrorCode && error) {
      Sentry.withScope((scope) => {
        scope.setTag('owner', sentryConstants.TEAMS.VISUAL);
        Sentry.captureException(new Error('Unhandled build error'), {
          extra: {
            cause: error,
          },
        });
      });
    }
  }, [error, isErrorCode]);

  return (
    <>
      {isRunning && (
        <div className="p-2">
          <InlineAlert
            color="info"
            title="This build is executing in real time"
          >
            Snapshots will appear here as the build progresses.
            We&apos;ll reload the page automatically if the build status changes.
          </InlineAlert>
        </div>
      )}
      {isErrored && (
        <div className="p-2">
          <InlineAlert
            color="warning"
            title={errorMsg}
          >
            {errorDescription}
          </InlineAlert>
        </div>
      )}
      {(isSuccess && showSuccess) && (
        <div className="fixed top-9 z-1000 sm:w-50 left-0 right-0 mx-auto px-2">
          <ToastAlert
            color="success"
            onDismiss={() => setShowSuccess(false)}
            title="Build is now completed"
          >
            You can review the snapshots
          </ToastAlert>
        </div>
      )}
    </>
  );
};

export default DiffHeaderStatus;
